import { Box, Grid, List, Stack, styled } from '@mui/material';
import { Dispatch, SetStateAction, useState } from 'react';
import { FormProvider, useFormContext, SubmitHandler, FieldValues } from 'react-hook-form';
import { MonsterSearch } from 'wisestep-api';
import { IFilterList, WsButton, WsDrawer, WsFilterTags, WsSelect } from 'wisestep-base';

import CheckboxListFilter from '../../../../components/filters/components/CheckboxListFilter';
import JobTitleFilter from '../../../../components/filters/components/JobTitleFilter';
import SalaryRangeFilter from '../../../../components/filters/components/SalaryRangeFilter';
import WsAccordion from '../../../../components/filters/components/WsAccordion';
import BasicSearchForm from '../../types/IBasicSearchForm';
import { languageOptions, languageProficiencyOptions } from '../utils/MonsterHelper';

import OccupationFilter from './OccupationFilter';
import PowerSearchAdvanceFilter from './PowerSearchAdvancedFilters';
import SelectFilters from './SelectFilter';

interface AdvanceFilterProps {
  open: boolean;
  onClose: () => void;
  performSearch: (values: BasicSearchForm & MonsterSearch) => void;
  setFilterTags: Dispatch<SetStateAction<IFilterList[]>>;
}

const DrawerFooter = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
  position: 'fixed',
  bottom: 1,
  left: 2,
  right: 2,
  justifyContent: 'flex-end',
}));

const AdvanceFilter = ({ open, onClose, performSearch }: AdvanceFilterProps) => {
  const methods = useFormContext();
  const { handleSubmit, reset, watch, setValue, getValues } = methods;
  const searchType = watch('searchType');
  const [languageTags, setLanguageTags] = useState<IFilterList[]>([]);
  const getNewLanguageTag = (languageIndex: string, proficiencyIndex: string) => {
    return {
      tag: `${languageOptions.find((item) => item.value == languageIndex)?.label} : ${
        languageProficiencyOptions.find((item) => item.value == proficiencyIndex)?.label
      }`,
      id: languageIndex,
    };
  };

  const onDeleteLanguageTag = (tag: string) => {
    setLanguageTags((prevTags) => {
      const newTags = prevTags.filter((languageTag) => languageTag.tag !== tag);

      const newLSkillValues = newTags.map((item) => {
        const languageLabel = item.tag.split(' : ')[0];
        const proficiencyLabel = item.tag.split(' : ')[1];
        const languageValue = languageOptions.find((item2) => item2.label === languageLabel)?.value;
        const proficiencyValue = languageProficiencyOptions.find(
          (item2) => item2.label === proficiencyLabel
        )?.value;

        return `${languageValue}-${proficiencyValue}`;
      });

      setValue('lskill', newLSkillValues);
      return newTags;
    });
  };

  const addLanguage = () => {
    const language = getValues('language');
    const proficiency = getValues('proficiency');

    const lskillValue = getValues('lskill');
    const lskillArray = lskillValue ? lskillValue.split(',') : [];
    lskillArray.push(`${language}-${proficiency}`);
    const lskillString = lskillArray.join(',');
    setValue('lskill', lskillString);

    setLanguageTags([...languageTags, getNewLanguageTag(language, proficiency)]);
  };

  return (
    <FormProvider {...methods}>
      <WsDrawer
        title="Advanced Filters"
        open={open}
        onClose={() => onClose()}
        size="medium"
        headerSize="medium"
      >
        <Box
          component="form"
          role="presentation"
          onSubmit={handleSubmit(performSearch as SubmitHandler<FieldValues>)}
          onReset={() => reset()}
        >
          {searchType === 'Boolean' ? (
            <Box pb={7}>
              <List>
                <SelectFilters
                  name="qb"
                  title="Keyword query behavior"
                  selectArray={[
                    {
                      label: 'Select',
                      value: '',
                    },
                    {
                      label: 'And',
                      value: 'and',
                    },
                    {
                      label: 'Or',
                      value: 'or',
                    },
                    {
                      label: 'Boolean',
                      value: 'bool',
                    },
                  ]}
                />
                <JobTitleFilter
                  name="qrjtArr"
                  placeHolder="Recent Job Title"
                  title="Recent Job Title"
                />
                <JobTitleFilter
                  name="qtjtArr"
                  placeHolder="Target Job Title"
                  title="Target Job Title"
                />
                <JobTitleFilter
                  name="qrcnArr"
                  placeHolder="Recent Company Name"
                  title="Recent Company Name"
                />
                <JobTitleFilter
                  name="qrjdArr"
                  placeHolder="Recent Job Description"
                  title="Recent Job Description"
                />
                <JobTitleFilter
                  name="qsnArr"
                  placeHolder="Recent school name"
                  title="Recent School Name"
                />
                <SelectFilters
                  name="targetJobType"
                  title="Target Job Type"
                  selectArray={[
                    {
                      label: 'Any',
                      value: '',
                    },
                    {
                      label: 'Permanent',
                      value: 'tjtp',
                    },
                    {
                      label: 'Contract',
                      value: 'tjttc',
                    },
                    {
                      label: 'Full Time',
                      value: 'tjtft',
                    },
                    {
                      label: 'Part Time',
                      value: 'tjtpt',
                    },
                    {
                      label: 'Intern',
                      value: 'tjti',
                    },
                    {
                      label: 'Per Diem',
                      value: 'tjtpd',
                    },
                    {
                      label: 'Seasonal',
                      value: 'tjts',
                    },
                  ]}
                />
                <SelectFilters
                  name="edulv"
                  title="Education level"
                  selectArray={[
                    {
                      label: 'Select',
                      value: '',
                    },
                    {
                      label: 'High School or equivalent',
                      value: '1',
                    },
                    {
                      label: 'Certification',
                      value: '2',
                    },
                    {
                      label: 'Vocational',
                      value: '3',
                    },
                    {
                      label: 'Associate Degree',
                      value: '4',
                    },
                    {
                      label: 'Bachelors Degree',
                      value: '5',
                    },
                    {
                      label: 'Masters Degree',
                      value: '6',
                    },
                    {
                      label: 'Doctorate',
                      value: '7',
                    },
                    {
                      label: 'Professional',
                      value: '8',
                    },
                    {
                      label: 'Some College Coursework Completed',
                      value: '9',
                    },
                    {
                      label: 'Vocational – High School',
                      value: '10',
                    },
                    {
                      label: 'Vocational – Degree',
                      value: '11',
                    },
                  ]}
                />
                <SelectFilters
                  name="relo"
                  title="Willing to Relocate"
                  selectArray={[
                    {
                      label: 'Select',
                      value: '',
                    },
                    {
                      label: 'Yes',
                      value: '1',
                    },
                    {
                      label: 'No',
                      value: '0',
                    },
                  ]}
                />
                <SelectFilters
                  name="twsite"
                  title="Work Site"
                  selectArray={[
                    {
                      label: 'Select',
                      value: '',
                    },
                    {
                      label: 'On Site',
                      value: '1',
                    },
                    {
                      label: 'Off Site',
                      value: '2',
                    },
                    {
                      label: 'No Preference',
                      value: '3',
                    },
                  ]}
                />
                <WsAccordion title="Work authorization">
                  <WsSelect
                    name="wa"
                    selectArray={[
                      {
                        label: 'Select',
                        value: '',
                      },
                      {
                        label: 'Authorized to work for any employer',
                        value: '1',
                      },
                      {
                        label: 'Authorized to work for current employer only',
                        value: '2',
                      },
                      {
                        label: 'Require sponsorship',
                        value: '3',
                      },
                    ]}
                    placeholder="Select"
                  />
                </WsAccordion>

                <SalaryRangeFilter
                  title="Desired Salary"
                  selectArray={[
                    { label: 'Argentina Peso', value: '3' },
                    { label: 'Australia Dollar', value: '4' },
                    { label: 'Belgium Franc', value: '5' },
                    { label: 'Brazil Real', value: '6' },
                    { label: 'Canada Dollar', value: '7' },
                    { label: 'China Yuan Renmimbi', value: '9' },
                    { label: 'Czech Republic Koruna', value: '10' },
                    { label: 'Danish Krone', value: '38' },
                    { label: 'Euro', value: '2' },
                    { label: 'Fiji Dollar', value: '13' },
                    { label: 'Finnish Mark', value: '39' },
                    { label: 'France Franc', value: '14' },
                    { label: 'Germany Deutsche Mark', value: '11' },
                    { label: 'Greece Drachma', value: '16' },
                    { label: 'Hong Kong Dollar', value: '17' },
                    { label: 'Hungary Forint', value: '18' },
                    { label: 'India Rupee', value: '22' },
                    { label: 'Indonesia Rupiah', value: '19' },
                    { label: 'Ireland Punt', value: '20' },
                    { label: 'Israel New Shekel', value: '21' },
                    { label: 'Italy Lira', value: '23' },
                    { label: 'Japan Yen', value: '24' },
                    { label: 'Luxemborg Franc', value: '36' },
                    { label: 'Malaysia Ringgit', value: '27' },
                    { label: 'Mexico Peso', value: '26' },
                    { label: 'Netherlands Guilder', value: '28' },
                    { label: 'New Zealand Dollar', value: '29' },
                    { label: 'Norwegian Krone', value: '37' },
                    { label: 'Poland Zloty', value: '30' },
                    { label: 'Russia Ruble', value: '31' },
                    { label: 'Singapore Dollar', value: '33' },
                    { label: 'South Africa Rand', value: '35' },
                    { label: 'South Korea Won', value: '25' },
                    { label: 'Spain Peseta', value: '12' },
                    { label: 'Sweden Krona', value: '32' },
                    { label: 'Switzerland Franc', value: '8' },
                    { label: 'Taiwan Dollar', value: '34' },
                    { label: 'United Kingdom Pound', value: '15' },
                    { label: 'US Dollar', value: '1' },
                  ]}
                  selectArray1={[
                    { label: 'Yearly', value: '1' },
                    { label: 'Hourly', value: '2' },
                    { label: 'Weekly', value: '3' },
                    { label: 'Monthly', value: '4' },
                  ]}
                />
                <SelectFilters
                  name="mgrex"
                  title="Managerial Experience"
                  selectArray={[
                    {
                      label: 'Select',
                      value: '',
                    },
                    {
                      label: 'No',
                      value: '1',
                    },
                    {
                      label: 'Yes',
                      value: '2',
                    },
                  ]}
                />
                <SelectFilters
                  name="yrsexpid"
                  title="Years of Experience"
                  selectArray={[
                    {
                      label: 'Select',
                      value: '',
                    },
                    {
                      label: 'Less than 1 Year',
                      value: '1',
                    },
                    {
                      label: '1+ to 2 Years',
                      value: '2',
                    },
                    {
                      label: '2+ to 5 Years',
                      value: '3',
                    },
                    {
                      label: '5+ to 7 Years',
                      value: '4',
                    },
                    {
                      label: '7+ to 10 Years',
                      value: '5',
                    },
                    {
                      label: '10+ to 15 Years',
                      value: '6',
                    },
                    {
                      label: 'more than 15 Years',
                      value: '7',
                    },
                  ]}
                />
                <WsAccordion title="Language">
                  <Grid container spacing={2} direction="row">
                    <Grid item xs={5}>
                      <WsSelect
                        name="language"
                        selectArray={languageOptions}
                        placeholder="Language"
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <WsSelect
                        name="proficiency"
                        selectArray={languageProficiencyOptions}
                        placeholder="Proficiency"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <WsButton variant="contained" size="small" onClick={addLanguage}>
                        Add
                      </WsButton>
                    </Grid>
                  </Grid>
                  <WsFilterTags
                    filtersList={languageTags}
                    onDelete={onDeleteLanguageTag}
                    label="Languages to be filtered"
                  />
                </WsAccordion>

                <SelectFilters
                  name="twindus"
                  title="Target Work Industry"
                  selectArray={[
                    { label: 'All', value: '0' },
                    { label: 'Agriculture/Forestry/Fishing', value: '1' },
                    { label: 'Metals and Minerals', value: '2' },
                    { label: 'Energy and Utilities', value: '3' },
                    {
                      label: 'Construction - Industrial Facilities and Infrastructure',
                      value: '4',
                    },
                    { label: 'Trade Contractors', value: '5' },
                    { label: 'Aerospace and Defense', value: '6' },
                    { label: 'Automotive and Parts Mfg', value: '7' },
                    { label: 'Biotechnology/Pharmaceuticals', value: '8' },
                    { label: 'Chemicals/Petro-Chemicals', value: '9' },
                    { label: 'Consumer Packaged Goods Manufacturing', value: '10' },
                    { label: 'Electronics, Components, and Semiconductor Mfg', value: '11' },
                    { label: 'Manufacturing - Other', value: '12' },
                    { label: 'Printing and Publishing', value: '13' },
                    { label: 'Clothing and Textile Manufacturing', value: '14' },
                    { label: 'Wholesale Trade/Import-Export', value: '15' },
                    { label: 'Grocery/Convenience/Gas', value: '16' },
                    { label: 'Retail', value: '17' },
                    { label: 'Travel, Transportation and Tourism', value: '18' },
                    { label: 'Transport and Storage - Materials', value: '19' },
                    { label: 'Internet Services', value: '20' },
                    { label: 'Broadcasting, Music, and Film', value: '21' },
                    { label: 'Telecommunications Services', value: '22' },
                    { label: 'Banking', value: '23' },
                    { label: 'Insurance', value: '24' },
                    { label: 'Mortgage', value: '25' },
                    { label: 'Real Estate/Property Management', value: '26' },
                    { label: 'Rental Services', value: '27' },
                    { label: 'Accounting and Auditing Services', value: '28' },
                    { label: 'Advertising and PR Services', value: '29' },
                    { label: 'Architectural and Design Services', value: '30' },
                    { label: 'Management Consulting Services', value: '31' },
                    { label: 'Computer Hardware', value: '32' },
                    { label: 'Computer Software', value: '33' },
                    { label: 'Legal Services', value: '34' },
                    { label: 'Management and Holding Companies', value: '35' },
                    { label: 'Administrative and Support Services', value: '36' },
                    { label: 'Waste Management', value: '37' },
                    { label: 'Education', value: '38' },
                    { label: 'Healthcare Services', value: '39' },
                    { label: 'Nursing/Residential Care Facilities', value: '40' },
                    { label: 'Social Services', value: '41' },
                    { label: 'Performing and Fine Arts', value: '42' },
                    { label: 'Sports and Physical Recreation', value: '43' },
                    { label: 'Hotels and Lodging', value: '44' },
                    { label: 'Restaurant/Food Services', value: '45' },
                    { label: 'Staffing/Employment Agencies', value: '46' },
                    { label: 'Nonprofit Charitable Organizations', value: '47' },
                    { label: 'Personal and Household Services', value: '48' },
                    { label: 'Repair and Maintenance Services', value: '49' },
                    { label: 'Government and Military', value: '50' },
                    { label: 'Government- Military/Defense', value: '51' },
                    { label: 'Government- National', value: '52' },
                    { label: 'Security and Surveillance', value: '74' },
                    { label: 'Automotive Sales and Repair Services', value: '75' },
                    { label: 'Business Services - Other', value: '76' },
                    { label: 'Computer/IT Services', value: '77' },
                    { label: 'Construction - Residential & Commercial/Office', value: '78' },
                    { label: 'Engineering Services', value: '79' },
                    { label: 'Entertainment Venues and Theaters', value: '80' },
                    { label: 'Financial Services', value: '81' },
                    { label: 'Food and Beverage Production', value: '82' },
                    { label: 'Marine Mfg & Services', value: '83' },
                    { label: 'Medical Devices and Supplies', value: '84' },
                    { label: 'Other/Not Classified', value: '85' },
                  ]}
                />
                <OccupationFilter
                  title="Target Occupation"
                  name="tcc"
                  placeHolder="Job Category"
                  placeHolder1="Occupation"
                />
                <CheckboxListFilter
                  name="clvArr"
                  options={{
                    '10': 'Student (Undergraduate/Graduate)',
                    '11': 'Entry Level',
                    '12': 'Experienced (Non-Manager)',
                    '13': 'Manager (Manager/Supervisor of Staff)',
                    '14': 'Executive (SVP, VP, Department Head, etc)',
                    '15': 'Senior Executive (President, CFO, etc)',
                    '16': 'Student (High School)',
                  }}
                  title="Career Level"
                />
                <CheckboxListFilter
                  name="gscArr"
                  options={{
                    '11': 'None',
                    '12': 'Active Confidential',
                    '13': 'Active Secret',
                    '14': 'Active Top Secret',
                    '15': 'Inactive Confidential',
                    '16': 'Inactive Secret',
                    '17': 'Inactive Top Secret',
                  }}
                  title="Global Security Clearance"
                />
                <CheckboxListFilter
                  name="gmiArr"
                  options={{
                    '0': 'None',
                    '1': 'Unspecified',
                    '2': 'Active Duty',
                    '3': 'Retired Military',
                    '4': 'Veteran/Prior Service',
                    '5': 'Reservist (drilling)',
                    '6': 'National Guard',
                    '7': 'Inactive Reserve/Guard (not drilling)',
                    '8': 'Service Academy',
                    '9': 'In Military Preparation',
                    '10': 'Other Military Program',
                    '11': 'Government Employee',
                    '12': 'Defense Contractor',
                    '13': 'Considering Joining',
                    '14': 'Military Spouse',
                    '15': 'Spouse of a Veteran',
                    '16': 'Other Military Family Member',
                  }}
                  title="Global Military Status"
                />
              </List>
            </Box>
          ) : (
            <PowerSearchAdvanceFilter />
          )}
          <DrawerFooter>
            <Stack p={1} spacing={2} direction="row">
              <WsButton variant="outlined" size="large" type="reset">
                Reset
              </WsButton>
              <WsButton variant="contained" size="large" type="submit">
                Apply
              </WsButton>
            </Stack>
          </DrawerFooter>
        </Box>
      </WsDrawer>
    </FormProvider>
  );
};

export default AdvanceFilter;
